/**
 * `useFileBytes` — fetch a workspace file via `document.read_bytes`
 * and expose it as an object-URL Blob that viewer components can
 * pass to `<iframe src>` / `<img src>` / `<video src>` directly.
 *
 * Closes the SPA half of the viewer-pane build (per
 * ai-docs/lexide-layout-plan.md §3.5 — replacing the placeholder
 * `PdfViewerStub`/`ImageViewerStub`/`TextViewerStub` with real
 * viewers). Backend: lexruntime commit `a5c3bf7b`.
 *
 * Lifecycle:
 *   1. On mount / path change, fetches the RPC.
 *   2. Decodes the base64 → ArrayBuffer → Blob with the inferred
 *      mimeType.
 *   3. `URL.createObjectURL(blob)` mints a lived browser URL the
 *      <iframe>/<img>/etc. can target directly (no second round
 *      trip through the SPA's own server).
 *   4. On unmount OR path change, `URL.revokeObjectURL` releases
 *      the browser's reference so the Blob can be GC'd.
 *
 * Returns the standard react-query `{data, isPending, isError, error}`
 * shape so callers can render loading + error states cleanly.
 *
 * Caveats:
 *   - The full file lives in JS heap memory for the duration the
 *     viewer is open (acceptable for the WS-frame-bounded 18 MB cap).
 *   - The Blob URL is per-tab; not shareable across tabs.
 *   - For files >18 MB the RPC rejects with INVALID_PARAMS "exceeds
 *     preview limit"; the hook surfaces this as `isError`/`error.message`.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  useActiveContextStore,
  useEffectiveScope,
} from "@/stores/useActiveContextStore";
import {
  scopeKeySegment,
  workspaceQueryEnabled,
} from "@/lib/workspace-scope";

export interface UseFileBytesResult {
  /** Object URL the viewer can pass to <iframe src> / <img src>. */
  url: string | null;
  /** Inferred MIME type (e.g. "application/pdf"). Available even
   *  while `url` is null if the RPC has resolved. */
  mimeType: string | null;
  /** Decoded byte length. */
  byteLength: number | null;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  /** Manually re-fetch (e.g. retry after a transient WS hiccup). */
  refetch: () => void;
}

/**
 * Decode a base64 string to an `ArrayBuffer` via `atob`. Used in
 * place of `Buffer.from(b64, "base64")` because Node's `Buffer`
 * isn't a browser primitive; `atob` is.
 *
 * For empty input (`b64 === ""`) returns a 0-byte buffer. Throws
 * `DOMException` on invalid base64 (the consumer's react-query
 * wraps that into `isError`).
 */
function base64ToArrayBuffer(b64: string): ArrayBuffer {
  if (b64 === "") return new ArrayBuffer(0);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function useFileBytes(path: string | null): UseFileBytesResult {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const selection = useActiveContextStore((s) => s.selection);
  const scope = useEffectiveScope();

  const clientId = selection?.clientId;
  const matterId = selection?.matterId;
  const caseId = selection?.caseId;

  // CCM Wave 2 — org-scope branch. When the effective scope is global
  // (selection null → org), open the file against the per-user "Global"
  // workspace via `documentReadBytesOrg` (`{scope:"org", path}`, R3).
  // Otherwise the existing case path. The viewer components don't change
  // — they call this hook and render whatever bytes it returns.
  const isOrg = scope === "org";

  const query = useQuery({
    // Scope-distinct key: org rows live under "org", case rows under the
    // caseId, so a case ↔ org switch never serves the wrong file's bytes
    // from cache (a same-named `sources/x.pdf` in both scopes is distinct).
    queryKey: ["document-read-bytes", scopeKeySegment(scope, caseId ?? undefined), path],
    queryFn: async () => {
      if (!rpc) throw new Error("Gateway not connected");
      if (!path) throw new Error("No file path");
      if (isOrg) {
        return rpc.documentReadBytesOrg({ scope: "org", path });
      }
      // D-8: caseId alone suffices (document.read_bytes resolves by caseId via
      // resolveGatewayWorkspace); a flat/unassigned case has null parents.
      if (!caseId) {
        throw new Error("No active case scope");
      }
      return rpc.documentReadBytes({
        clientId: clientId ?? undefined,
        matterId: matterId ?? undefined,
        caseId,
        path,
      });
    },
    enabled:
      Boolean(rpc) &&
      connectionState === "connected" &&
      workspaceQueryEnabled(scope, {
        clientId: clientId ?? undefined,
        matterId: matterId ?? undefined,
        caseId: caseId ?? undefined,
      }) &&
      Boolean(path),
    // Files don't change in-place under raw-intake/ (audit-floor
    // invariant) or sources/ (mission produces new files, not
    // mutates existing). 5-minute stale is safe + keeps a re-open
    // of the same file instant.
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // Mint + tear down the Blob URL in sync with the query data.
  // `URL.createObjectURL` mints a tab-scoped URL; `revokeObjectURL`
  // releases the browser reference so the Blob can be GC'd. We
  // also revoke on unmount via the effect cleanup.
  const [url, setUrl] = useState<string | null>(null);
  // Decode failures are their OWN error channel.
  //
  // This used to `catch { setUrl(null) }` under a comment claiming the
  // failure "surfaces via `query.error` paths". It does not: the RPC
  // SUCCEEDED, so `query.error` is null and `query.isError` is false —
  // the read resolves reassuringly while the consumer is handed
  // `url: null, isPending: false, isError: false`, a settled state that
  // means nothing. Every consumer then falls through to whichever branch
  // it happens to check last: the older viewers render the "empty" cue,
  // and `useSourceExtraction` spun on "loading" forever. A failure path
  // that resolves successfully is the exact defect class this codebase
  // keeps re-deriving, so the decode error is now reported as an error.
  const [decodeError, setDecodeError] = useState<Error | null>(null);
  useEffect(() => {
    if (!query.data) {
      setUrl(null);
      setDecodeError(null);
      return;
    }
    let mintedUrl: string | null = null;
    try {
      const buf = base64ToArrayBuffer(query.data.bytes);
      const blob = new Blob([buf], { type: query.data.mimeType });
      mintedUrl = URL.createObjectURL(blob);
      setUrl(mintedUrl);
      setDecodeError(null);
    } catch (err) {
      setUrl(null);
      setDecodeError(err as Error);
    }
    return () => {
      if (mintedUrl) URL.revokeObjectURL(mintedUrl);
    };
  }, [query.data]);

  return {
    url,
    mimeType: query.data?.mimeType ?? null,
    byteLength: query.data?.byteLength ?? null,
    isPending: query.isPending,
    isError: query.isError || decodeError !== null,
    error: (query.error as Error | null) ?? decodeError,
    refetch: () => {
      void query.refetch();
    },
  };
}
